import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { usePermission } from '../../../../auth/AuthContext';
import { personnelApi } from '../personnelApi';
import {
  EMPLOYMENT_TYPE_LABELS,
  type EmploymentType,
  type PersonnelCatalogEntry,
  type PersonnelEmploymentRecord,
  type PlantillaVacantItem,
} from '../personnelTypes';
import { sampleEmploymentRecord } from '../utils/sampleEmploymentRecord';
import {
  EMPTY_POST_SELECTION,
  PostPickers,
  postSelectionOf,
  postSelectionValid,
  resolveStagedItem,
  type PostSelection,
} from './sections/PostPickers';

/**
 * Records a post at THIS organization in the employment history — which plantilla item this
 * employee occupies, from when. A previous employer is `<PreviousEmploymentModal>`, under a
 * different key: this one fills a slot, so it is `personnel.assign`.
 *
 * **Two fields arrived with the 27 Oct 2026 merge** — status of appointment and monthly salary,
 * both optional. Government or private is NOT asked: a plantilla item is a government post, and
 * the server sets it.
 *
 * **It RECORDS rather than appoints, and the wording is the change.** Product call,
 * 15 Sep 2026. "Appoint" names a decision somebody else made in a document this system does not
 * hold; what happens here is a clerk writing down where a person is posted. The distinction
 * matters because the form carries no approval, no order number and no authority — presenting
 * it as the act of appointing claimed a weight it does not have.
 *
 * **It renders the create form's own pickers**, because they are the same question: which
 * unit, which post, which seat, from when, on what paper. `<PostPickers>` is that question —
 * shared rather than copied, since the last time each form held its own copy one of them grew
 * a third picker and this one never did.
 *
 * **One field is here that the wizard does not offer: adding a nature of appointment in
 * place.** That step is already five pickers deep and a reader creating an employee is not the
 * reader curating a vocabulary; a reader recording a promotion is exactly who meets a word the
 * catalog is missing.
 *
 * **Several open posts are allowed**, and exactly one of them is primary — see the
 * checkbox below for what that decides.
 */
export function EmploymentRecordModal({
  personnelId,
  hasOpenPost,
  currentPrimary,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Drives whether primary is a question at all — the first post is always it. */
  hasOpenPost: boolean;
  /** The open line the tick takes the flag from, named in the warning; null when none holds it. */
  currentPrimary: PersonnelEmploymentRecord | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [vacant, setVacant] = useState<PlantillaVacantItem[]>([]);

  /*
    UNIT, POST and SEAT as ONE value — they are one decision, and the REQUEST still carries the
    seat alone: the first two narrow the picker, and the row is written against an item.
  */
  const [post, setPost] = useState<PostSelection>(EMPTY_POST_SELECTION);
  const [effectiveFrom, setEffectiveFrom] = useState('');

  /*
    The appointment behind the line of service. All three are optional: a record can be filed
    before the instrument is in hand, and a required field the reader cannot answer is a form
    they cannot submit.
  */
  const [appointmentDate, setAppointmentDate] = useState('');
  const [assumptionDate, setAssumptionDate] = useState('');
  const [appointmentNatureId, setAppointmentNatureId] = useState<string | null>(null);

  /*
    Merged from work experience (27 Oct 2026). Both optional: the status may be the record's own
    employment type or something else on a later line, and a salary is what was stated, never
    read off the grade the post sits at.
  */
  const [appointmentStatus, setAppointmentStatus] = useState<EmploymentType | null>(null);
  const [monthlySalary, setMonthlySalary] = useState('');
  const [natures, setNatures] = useState<PersonnelCatalogEntry[]>([]);
  const [isPrimary, setIsPrimary] = useState(!hasOpenPost);
  const [remarks, setRemarks] = useState('');

  /*
    The catalog's OWN key, not this form's. Adding a word every future post can be
    filed under is a different grant from recording that one employee moved — so the Add row is
    withheld from a reader without it rather than offered and 403'd.
  */
  const canAddNature = usePermission('personnel.manage-catalogs');

  const [loadingVacant, setLoadingVacant] = useState(true);
  const [loadingNatures, setLoadingNatures] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    personnelApi
      .vacantItems(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setVacant(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not load the plantilla.', 0),
        );

        // No focus call: a failed load names no field.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingVacant(false);
      });

    return () => controller.abort();
  }, []);

  /*
    The nature catalog, in its own effect and its own loading flag. A failure here must not
    blank the position picker: the two are independent feeds, and a form that cannot offer a
    vocabulary can still record where somebody is posted.
  */
  useEffect(() => {
    const controller = new AbortController();

    personnelApi
      .catalogs(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setNatures(next.appointmentNatures);
      })
      .catch(() => {
        // No banner and no focus call: a failed catalog load names no field, and the field it
        // fills is optional. The picker renders empty and the rest of the form still saves.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingNatures(false);
      });

    return () => controller.abort();
  }, []);

  /*
    Checked here so the reader is told before submitting, and on the server so it holds for any
    caller. Nobody assumes a post before it is given to them.
  */
  const assumedBeforeAppointed =
    appointmentDate !== '' && assumptionDate !== '' && assumptionDate < appointmentDate;

  const salaryValue = monthlySalary.trim() === '' ? null : Number(monthlySalary);
  const salaryValid = salaryValue === null || (Number.isFinite(salaryValue) && salaryValue >= 0);

  const valid =
    postSelectionValid(post) && effectiveFrom.length > 0 && !assumedBeforeAppointed && salaryValid;
  const canSave = valid && confirmed && !saving;

  function generate() {
    const sample = sampleEmploymentRecord(vacant, natures);

    if (!sample) return;

    setPost(postSelectionOf(sample.item));
    setEffectiveFrom(sample.effectiveFrom);
    setAppointmentDate(sample.appointmentDate);
    setAssumptionDate(sample.assumptionDate);

    // A real row out of the catalog the form already loaded — an invented id fails the
    // server's "it must exist and be active" check, which reads as a bug in the form.
    setAppointmentNatureId(sample.appointmentNatureId);
    setAppointmentStatus(sample.appointmentStatus);
    setMonthlySalary(sample.monthlySalary);
    setRemarks(sample.remarks);
  }

  /**
   * Writes the catalog row, then selects the id it comes back with.
   *
   * The picker is over a FOREIGN KEY, so committing the typed text — what a bare `allowCustom`
   * does — would put a label where an id belongs. The create is idempotent on the name, so two
   * readers adding "Reemployment" at once get one row and no error.
   */
  async function createNature(raw: string) {
    const name = raw.trim();
    if (!name) return;

    try {
      const created = await personnelApi.addAppointmentNature(name);

      setNatures((current) =>
        current.some((n) => n.id === created.id) ? current : [...current, created],
      );
      setAppointmentNatureId(created.id);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError('Could not add that nature of appointment.', 0),
      );
    }
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      /*
        WHATEVER THE PICKERS STAGED IS WRITTEN FIRST — a post the unit had none of, or a seat
        the reader typed — immediately before this row and never on a picker's press. Both are
        the plantilla's own calls under the plantilla's own keys. Residual gap: a line refused
        after this leaves the new post or seat behind, vacant.
      */
      const slotId = await resolveStagedItem(post);

      if (slotId === null) {
        setError(new ApiError('Could not reserve that plantilla item.', 0));
        return;
      }

      await personnelApi.addEmploymentRecord(personnelId, {
        /*
          The exact slot the reader chose. The list it came from is a moment old, so the server
          re-checks vacancy inside the write — a slot taken in between comes back as a rejection
          rather than a double booking.
        */
        plantillaItemId: slotId,
        effectiveFrom,

        // Empty is ABSENT, not a blank date: the server distinguishes "not recorded" from a
        // value, and "" would be neither.
        appointmentDate: appointmentDate || null,
        assumptionDate: assumptionDate || null,
        appointmentNatureId,
        appointmentStatus,
        monthlySalary: salaryValue,
        isPrimary,
        remarks: remarks.trim() || null,
      });

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not record the post.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'employment-record' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Add post at this organization"
      size="lg"
      id="employment-record-modal"
      autogenerate={{
        id: 'employment-record-autogenerate',
        onGenerate: generate,
        disabled: saving || loadingVacant || vacant.length === 0,
      }}
      confirm={{
        id: 'employment-record-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="employment-record-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="employment-record-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          UNIT → POSITION → ITEM, the same component the create wizard's Employment step
          renders. A reader who filled that form meets these pickers here behaving identically,
          because it is the same control rather than a second copy of it.
        */}
        <PostPickers
          idPrefix="employment-record"
          value={post}
          onChange={setPost}
          vacantItems={vacant}
          error={error}
          disabled={saving || loadingVacant}
        />

        {/*
          THREE dates, and they are not the same fact: the effectivity is the period this row
          records, the appointment is the paper somebody signed, and the assumption is the day
          they reported. They routinely differ by weeks.

          ONE grid, because a date field never takes a row of its own in a modal — `mm/dd/yyyy`
          plus a calendar button is a fixed, small amount of content, and a date stretched across
          both columns is a box mostly full of nothing. The third wraps onto its own row, which
          reads as the end of the run rather than as a stranded field.

          NEITHER of the two below is capped at today, unlike a hire date. An instrument dated
          ahead of the assumption is normal, and a future-date guard copied from that field would
          refuse a paper the reader is holding.
        */}
        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="employment-record-effective-from"
            label="Effective from"
            required
            description="The day this post takes effect — not the employee's hire date."
            value={effectiveFrom}
            onChange={setEffectiveFrom}
            error={
              effectiveFrom ? error?.fieldError('effectiveFrom') : 'Choose when it takes effect.'
            }
            disabled={saving}
          />

          <DateField
            id="employment-record-appointment-date"
            label="Appointment date"
            description="The day the appointment instrument is dated."
            value={appointmentDate}
            onChange={setAppointmentDate}
            error={error?.fieldError('appointmentDate')}
            disabled={saving}
          />

          <DateField
            id="employment-record-assumption-date"
            label="Assumption date"
            description="The day the employee assumed the duties of the post."
            value={assumptionDate}
            /*
              The client copy of the server's rule, so the reader is told before submitting
              rather than after. The server's is the one that holds.
            */
            min={appointmentDate || undefined}
            onChange={setAssumptionDate}
            error={
              assumedBeforeAppointed
                ? 'Choose a date on or after the appointment date.'
                : error?.fieldError('assumptionDate')
            }
            disabled={saving}
          />

          {/*
            The nature of the appointment — WHY the movement happened — beside the STATUS of the
            appointment, which is a different fact: one person can be permanently appointed by
            promotion, and both are on the form. Six fields, one grid, three rows of two.

            `allowCustom` with `onCreateOption`: an unmatched query offers Add "…" and the handler
            writes the catalog row under its own key before selecting it. Withheld from a reader
            without that key — hide, don't disable — and the sentence about adding one goes with
            it rather than promising an Add row they will never be shown.
          */}
          <Select
            id="employment-record-appointment-nature-id"
            label="Nature of appointment"
            loading={loadingNatures}
            description={
              canAddNature
                ? 'Why the movement happened — original appointment, promotion, transfer, reassignment. Type a value that is not listed to add it.'
                : 'Why the movement happened — original appointment, promotion, transfer, reassignment.'
            }
            allowCustom={canAddNature}
            onCreateOption={canAddNature ? createNature : undefined}
            value={appointmentNatureId}
            onChange={(next) => setAppointmentNatureId(next || null)}
            options={natures.map((n) => ({ value: n.id, label: n.name }))}
            sort="none"
            error={error?.fieldError('appointmentNatureId')}
            disabled={saving}
          />

          <Select
            id="employment-record-appointment-status"
            label="Status of appointment"
            description="Permanent, temporary, casual, contractual — the status of THIS appointment."
            value={appointmentStatus === null ? null : String(appointmentStatus)}
            onChange={(next) =>
              setAppointmentStatus(next === null || next === '' ? null : (Number(next) as EmploymentType))
            }
            options={Object.entries(EMPLOYMENT_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            // The form's own order — most to least secure tenure — not alphabetical.
            sort="none"
            error={error?.fieldError('appointmentStatus')}
            disabled={saving}
          />

          <TextField
            id="employment-record-monthly-salary"
            label="Monthly salary"
            type="number"
            min={0}
            step="0.01"
            description="What is actually paid, as stated. Never read from the salary schedule — the grade comes from the post."
            value={monthlySalary}
            onChange={(e) => setMonthlySalary(e.target.value)}
            error={
              salaryValid ? error?.fieldError('monthlySalary') : 'Enter a monthly salary of 0 or more.'
            }
            disabled={saving}
          />
        </div>

        {/*
          Only asked once there is something for it to be primary over. A first post is
          always primary, so offering the choice would be a control with one valid answer.

          The consequence is on the FORM, before the tick — never appended to it.
        */}
        {hasOpenPost ? (
          <label
            htmlFor="employment-record-primary"
            className="flex items-start gap-2 text-sm text-slate-600"
          >
            <Checkbox
              id="employment-record-primary"
              name="employment-record-primary"
              checked={isPrimary}
              onChange={() => setIsPrimary((v) => !v)}
              disabled={saving}
            />
            <span>
              Make this their primary post.
              <span className="block text-xs text-slate-500">
                An employee may hold several posts at once, and exactly one of them is primary.
                The primary one is what the record reads its grade, unit and reporting line from,
                and it is the salary grade payroll will compute against.
              </span>
            </span>
          </label>
        ) : null}

        {/*
          THE CONSEQUENCE OF THE TICK, named before the confirm tick — product call, 26 Oct 2026.
          The server MOVES the flag in the same write (`AddEmploymentRecordAsync`); it does not end
          or delete the old line, so the sentence says it stays open rather than "removed".
        */}
        {hasOpenPost && isPrimary && currentPrimary ? (
          <NextActionAlert
            id="employment-record-primary-replaces"
            tone="amber"
            icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
            title="This replaces the current primary post"
            description={
              <>
                {currentPrimary.orgUnitName} — {currentPrimary.positionTitle} (item{' '}
                <span className="font-mono">{currentPrimary.itemNumber}</span>) is primary now.
                Submitting makes this record primary instead; that one stays open as a secondary
                post and is not ended.
              </>
            }
          />
        ) : null}

        <TextField
          id="employment-record-remarks"
          label="Remarks"
          description="Why the post was recorded, when it needs saying."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={error?.fieldError('remarks')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
